/**
 * extraHoursAdapter — les catalogues, les règles et le corps de `POST /api/v1/extra-hour-requests`.
 *
 * Port littéral de `src/common/components/family/parentExtraHours/utils.js` et de
 * `ParentExtraHoursModal.js` : `HOUR_OPTIONS`, `DEADLINE_OPTIONS`, `formatOptions`,
 * `resolveExtraHourPricePerHour`, `computeAllowedDays`, `slotStringToApi`, `validateForm`
 * (`utils.js:8-16`, `:33-60`, `:100-122`, `:172-231`) et `transformSubmitData`
 * (`ParentExtraHoursModal.js:69-103`). La grille hebdomadaire est la grille unique
 * (`data/utility/constraintsGrid`, celle que `ConstraintsGridStep.js` dessine), telle que le
 * formulaire l'appelle : dimanche exclu, heures de Paris — la demande d'heures supplémentaires les
 * annonce comme telles (`extraHoursTimezoneNotice`) et ne les traduit pas.
 *
 * Le design system ne calcule ni prix ni seuil : il reçoit des libellés et des phrases.
 */
import moment from "moment"

import { filterSchoolSubjectsByClass } from "../../../../../data/utility/Utils"
import { constraintsGridDays } from "../../../../../data/utility/constraintsGrid"
import { euros } from "../../../ui/formatters/moneyFormatter"
import {
    type UIExtraHoursBilling,
    type UIExtraHoursDeadlineOption,
    type UIExtraHoursDraft,
    type UIExtraHoursFormat,
    type UIExtraHoursFormatOption,
    type UIExtraHoursRecapKind,
    type UIExtraHoursStep,
    type UISubject,
    type UITimeSlotDay,
} from "../../../ui/types"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

/** Le format d'une date exacte, côté demande (`utils.js:5`). */
export const EXTRA_HOURS_DATETIME_FORMAT = "YYYY-MM-DD HH:mm"

/** « Combien d'heures ? » — `HOUR_OPTIONS`. */
export const EXTRA_HOURS_HOUR_OPTIONS = [1, 2, 3]

/**
 * Le tarif catalogue, servi par le backend en `staticData.extra_hour_price_per_hour`
 * (`ExtraHourRequest::PRICE_PER_HOUR`). Les valeurs ci-dessous ne sont QUE le miroir hors ligne
 * du legacy, à ne jamais lire directement.
 */
const PRICE_PER_HOUR_FALLBACK: Record<string, number> = { shared: 14, individual: 25 }

/** La grille du backend d'abord, le miroir hors ligne ensuite (`utils.js:24-31`). */
export function resolveExtraHourPricePerHour(
    staticGrid: Record<string, number> | null | undefined,
    format: UIExtraHoursFormat
): number {
    const fromBackend = staticGrid?.[format]
    if (Number.isFinite(fromBackend) && (fromBackend as number) > 0) return fromBackend as number
    return PRICE_PER_HOUR_FALLBACK[format] ?? 0
}

/** « Quel format ? » — deux cartes, le tarif déjà écrit (`utils.js:40-60`). */
export function toExtraHoursFormatOptions(
    staticGrid: Record<string, number> | null | undefined
): UIExtraHoursFormatOption[] {
    return [
        {
            value: "shared",
            label: "Cours partagé",
            priceLabel: `${euros(resolveExtraHourPricePerHour(staticGrid, "shared"))}/h`,
            description: "Planification rapide",
            icon: "users",
        },
        {
            value: "individual",
            label: "Cours individuel",
            priceLabel: `${euros(resolveExtraHourPricePerHour(staticGrid, "individual"))}/h`,
            description: "Tuteur 100% dédié, horaire flexible",
            icon: "user",
        },
    ]
}

/** « Pour quand ? » — `DEADLINE_OPTIONS`, la troisième ouvrant le calendrier. */
export const EXTRA_HOURS_DEADLINE_OPTIONS: UIExtraHoursDeadlineOption[] = [
    { value: "3_days", label: "Sous 3 jours" },
    { value: "7_days", label: "Sous 7 jours" },
    { value: "custom", label: "Date précise", custom: true },
]

/**
 * Le prix TTC de la demande, déjà écrit — « 28 € » : le tarif du backend × les heures (`RecapStep.js:63-64`).
 * Le récapitulatif y passe les heures FACTURÉES (`extraHoursBilling`), pas les heures demandées.
 */
export function extraHoursTotalLabel(
    staticGrid: Record<string, number> | null | undefined,
    draft: Pick<UIExtraHoursDraft, "hours" | "format">
): string {
    return euros(resolveExtraHourPricePerHour(staticGrid, draft.format) * draft.hours)
}

/**
 * Ce que le backend dit de la facturation d'une demande, sur l'élève (`UserResource`) :
 * `can_use_next_invoice` est la règle même du BO (`ExtraHourPaymentService::canUseNextInvoice`),
 * `autonomy_hour_available` ne vaut quelque chose qu'en offre Autonomie (`null` sinon).
 */
export type ApiExtraHoursBillingFacts = {
    can_use_next_invoice?: boolean | null
    autonomy_hour_available?: boolean | null
} | null

/**
 * Les heures de cours partagé comprises chaque mois dans l'offre Autonomie, sur demande — le miroir
 * de `Subscription::AUTONOMY_HOURS_ON_REQUEST_PER_MONTH`, et sa seule copie dans l'app.
 */
export const AUTONOMY_INCLUDED_HOURS = 1

/**
 * L'heure du mois de l'offre Autonomie face à cette demande. `included` : elle la prend ;
 * `already_requested` : une demande du mois l'a déjà prise ; `individual` : un cours individuel ne
 * la prend jamais.
 */
export type ExtraHoursAutonomyHour = "included" | "already_requested" | "individual"

export type ExtraHoursBilling = {
    billing: UIExtraHoursBilling
    /** Un achat dès qu'une heure se facture, une demande quand rien ne se facture. */
    recapKind: UIExtraHoursRecapKind
    /** Absent hors Autonomie. */
    autonomyHour?: ExtraHoursAutonomyHour
    /** Les heures facturées : toutes, moins celles que l'heure du mois couvre. */
    billedHours: number
    /** Ce que l'heure du mois fait à cette demande, écrit pour le récapitulatif. Absent hors Autonomie. */
    autonomyNote?: string
}

/** La phrase sous le prix : ce que l'offre Autonomie ne facture pas, ou pourquoi elle facture. */
function autonomyNote(autonomyHour: ExtraHoursAutonomyHour, includedHours: number, billedHours: number): string {
    switch (autonomyHour) {
        case "already_requested":
            return "Votre heure du mois a déjà été demandée : ce cours vous est facturé au tarif ci-dessus."
        case "individual":
            return "Votre heure du mois se prend en cours partagé : ce cours individuel vous est facturé au tarif ci-dessus."
        default:
            if (billedHours === 0) {
                return "Votre heure du mois est comprise dans votre offre Autonomie : elle ne vous est pas facturée."
            }
            return billedHours === 1
                ? `${includedHours}h est comprise dans votre offre Autonomie : seule l'heure en plus vous est facturée.`
                : `${includedHours}h est comprise dans votre offre Autonomie : seules les ${billedHours}h en plus vous sont facturées.`
    }
}

/**
 * Quand et combien la demande se facture — la même règle que la note du ticket
 * (`ExtraHourRequestNoteFormatter`). L'heure du mois de l'Autonomie est UNE heure de cours
 * partagé : une demande partagée la prend tant qu'elle est libre, le reste se facture ; un cours
 * individuel se facture toujours. Sans prochain prélèvement, c'est un prélèvement immédiat. Et
 * rien à facturer, la dernière étape ne parle plus d'achat : elle valide une demande (`recapKind`).
 *
 * Un backend qui ne sert pas encore ces champs laisse le récapitulatif d'avant : prochain
 * prélèvement, pas de ligne Autonomie.
 */
export function extraHoursBilling(
    draft: Pick<UIExtraHoursDraft, "hours" | "format">,
    facts: ApiExtraHoursBillingFacts | undefined
): ExtraHoursBilling {
    const available = facts?.autonomy_hour_available
    const autonomyHour: ExtraHoursAutonomyHour | undefined =
        available == null
            ? undefined
            : draft.format !== "shared"
              ? "individual"
              : available
                ? "included"
                : "already_requested"

    const includedHours = autonomyHour === "included" ? Math.min(draft.hours, AUTONOMY_INCLUDED_HOURS) : 0
    const billedHours = draft.hours - includedHours
    const billing: UIExtraHoursBilling =
        billedHours === 0 ? "none" : facts?.can_use_next_invoice === false ? "immediate" : "next_invoice"

    return {
        billing,
        recapKind: billedHours === 0 ? "request" : "purchase",
        autonomyHour,
        billedHours,
        autonomyNote: autonomyHour == null ? undefined : autonomyNote(autonomyHour, includedHours, billedHours),
    }
}

const DAY_OF_WEEK_BY_LABEL: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
}

const DAY_LABEL_BY_INT: Record<number, string> = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
}

/** La semaine du formulaire de disponibilités, dimanche exclu — la grille unique, en heures de Paris. */
const WEEK: Array<{ day: string; label: string; times: string[] }> = constraintsGridDays()

/**
 * Les jours anglais atteignables entre demain et l'échéance (`utils.js:100-122`). `null` = aucune
 * restriction : le legacy laisse la grille entière tant que l'échéance précise n'a pas de date.
 *
 * Le routeur refuse toute occurrence au-delà de l'échéance : un créneau posé sur un jour qui
 * n'entre pas dans la fenêtre ne peut produire aucun cours.
 */
export function computeAllowedDays(
    deadlineType: string | null | undefined,
    customDeadline: string | null | undefined,
    now: moment.Moment = moment()
): Set<string> | null {
    const start = now.clone().add(1, "day").startOf("day")
    let end: moment.Moment
    if (deadlineType === "3_days") end = now.clone().add(3, "days").endOf("day")
    else if (deadlineType === "7_days") end = now.clone().add(7, "days").endOf("day")
    else if (deadlineType === "custom" && customDeadline) end = moment(customDeadline).endOf("day")
    else return null

    const allowed = new Set<string>()
    const cur = start.clone()
    while (cur.isSameOrBefore(end, "day") && allowed.size < 7) {
        allowed.add(DAY_LABEL_BY_INT[cur.day()])
        cur.add(1, "day")
    }
    return allowed
}

/** La grille proposée à cette échéance : un jour = une colonne, ses créneaux dedans. */
export function toExtraHoursDays(allowedDays: Set<string> | null): UITimeSlotDay[] {
    return WEEK.filter((day) => allowedDays == null || allowedDays.has(day.day)).map((day) => ({
        id: day.day,
        label: day.label,
        slots: day.times.map((time) => ({ id: `${day.day} ${time}`, label: time })),
    }))
}

export type ApiPreferredSlot = { day_of_week: number; hour: number }

/** « Monday 17h-18h » → `{ day_of_week: 1, hour: 17 }` (`utils.js:124-130`). */
export function slotStringToApi(slot: string): ApiPreferredSlot | null {
    const [day, range] = slot.split(" ")
    const dayOfWeek = DAY_OF_WEEK_BY_LABEL[day]
    const hour = parseInt(range?.split("h")[0], 10)
    if (dayOfWeek === undefined || Number.isNaN(hour)) return null
    return { day_of_week: dayOfWeek, hour }
}

/**
 * Les créneaux que l'échéance resserrée laisse tomber (`SharedSlotsStep.js:41-52`). Le parent
 * est revenu à l'étape 1 et a raccourci son délai : ses cases d'un jour devenu inatteignable
 * n'auraient produit aucun cours.
 */
export function purgeSlotIdsOutOfWindow(slotIds: string[], allowedDays: Set<string> | null): string[] {
    if (allowedDays == null) return slotIds
    return slotIds.filter((slot) => allowedDays.has(slot.split(" ")[0]))
}

/**
 * Une ligne de date par heure demandée (`IndividualDatesStep.js:50-60`). La place existe avant
 * la valeur : le parent voit combien de dates on attend de lui.
 */
export function alignRequestedDates(requestedDates: Array<string | null>, hours: number): Array<string | null> {
    if (requestedDates.length === hours) return requestedDates
    const next = [...requestedDates]
    while (next.length < hours) next.push(null)
    while (next.length > hours) next.pop()
    return next
}

/** Le brouillon d'ouverture (`ParentExtraHoursModal.js:45-58`). */
export function initialExtraHoursDraft(): UIExtraHoursDraft {
    return {
        hours: 1,
        format: "shared",
        deadlineType: "7_days",
        customDeadline: null,
        subjectId: null,
        slotIds: [],
        requestedDates: [],
    }
}

/**
 * Les bornes du calendrier, pour l'échéance précise comme pour les dates exactes : trois jours
 * de délai — l'équipe en a besoin pour confirmer un tuteur — et trente jours d'horizon
 * (`ParametersStep.js:394-397`, `IndividualDatesStep.js:39-44`).
 */
export function extraHoursDateBounds(now: moment.Moment = moment()): { minDate: Date; maxDate: Date } {
    return {
        minDate: now.clone().add(3, "days").startOf("day").toDate(),
        maxDate: now.clone().add(30, "days").endOf("day").toDate(),
    }
}

export type ApiExtraHoursRequestBody = {
    hours: number
    format: UIExtraHoursFormat
    deadline_type: string
    custom_deadline: string | null
    subject_id: string | number | null
    preferred_slots?: ApiPreferredSlot[]
    requested_dates?: string[]
}

/**
 * Le corps de `createExtraHourRequest`, à l'identique de `transformSubmitData`
 * (`ParentExtraHoursModal.js:73-103`). En individuel, `requested_dates` court-circuite le
 * routage automatique côté serveur ; en partagé, `preferred_slots` l'alimente.
 */
export function extraHoursRequestBody(draft: UIExtraHoursDraft): ApiExtraHoursRequestBody {
    const base = {
        hours: draft.hours,
        format: draft.format,
        deadline_type: draft.deadlineType,
        custom_deadline:
            draft.deadlineType === "custom" && draft.customDeadline
                ? moment(draft.customDeadline).format("YYYY-MM-DD")
                : null,
        subject_id: draft.subjectId,
    }

    if (draft.format === "individual") {
        return {
            ...base,
            requested_dates: draft.requestedDates.filter(
                (value): value is string =>
                    typeof value === "string" && moment(value, EXTRA_HOURS_DATETIME_FORMAT, true).isValid()
            ),
        }
    }

    return {
        ...base,
        preferred_slots: draft.slotIds.map(slotStringToApi).filter((slot): slot is ApiPreferredSlot => slot != null),
    }
}

/** Une échéance précise n'est un choix qu'une fois la date posée. */
function customDeadlineMissing(draft: UIExtraHoursDraft): boolean {
    return draft.deadlineType === "custom" && !draft.customDeadline
}

/** Une date par heure demandée — EXACTEMENT, c'est ce que le back exige aussi. */
function dateCountMatchesHours(draft: UIExtraHoursDraft): boolean {
    return draft.requestedDates.length === draft.hours
}

/**
 * « Cette étape est-elle remplie ? » — le verdict qui allume « Continuer », étape par étape.
 *
 * Il lit les mêmes règles que `validateExtraHoursDraft`, qui juge la demande ENTIÈRE au moment de
 * l'envoi : le gabarit les redérivait de son côté, et les deux phrasés avaient déjà divergé sur le
 * compte des dates (« au moins une par heure » contre « exactement une par heure »). C'est
 * l'exactitude qui gagne — sinon « Continuer » s'allume sur une demande que l'envoi refusera.
 *
 * Les étapes sans choix à remplir (récapitulatif, accusé) répondent oui : ce qui refuse l'envoi
 * est la liste d'erreurs, pas ce verdict.
 */
export function extraHoursStepComplete(draft: UIExtraHoursDraft, step: UIExtraHoursStep): boolean {
    switch (step) {
        case "parameters":
            // Le format individuel n'a pas d'échéance : sa date EST la demande.
            return draft.subjectId != null && (draft.format === "individual" || !customDeadlineMissing(draft))
        case "slots":
            return draft.slotIds.length > 0
        case "dates":
            return dateCountMatchesHours(draft) && draft.requestedDates.every((value) => value != null && value !== "")
        default:
            return true
    }
}

/**
 * Les règles locales, miroir de `StoreExtraHourRequestRequest` (`utils.js:177-231`). Elles
 * arrivent au design system déjà rédigées : aucun seuil (1 à 3 heures, 3 jours, 30 jours,
 * doublons) ne s'écrit dans un composant.
 */
export function validateExtraHoursDraft(draft: UIExtraHoursDraft, now: moment.Moment = moment()): string[] {
    const errors: string[] = []
    const { hours, format, deadlineType, customDeadline, subjectId } = draft

    if (!hours || hours < 1 || hours > 3) errors.push("Choisissez entre 1 et 3 heures.")
    if (!format) errors.push("Choisissez un format (cours partagé ou individuel).")
    if (!deadlineType) {
        errors.push("Choisissez une échéance.")
    } else if (deadlineType === "custom") {
        if (customDeadlineMissing(draft)) {
            errors.push("Choisissez une date d'échéance précise.")
        } else {
            const picked = moment(customDeadline)
            const min = now.clone().startOf("day").add(3, "days")
            if (!picked.isValid() || picked.isBefore(min)) {
                errors.push(
                    "L'échéance doit être au minimum dans 3 jours (sinon utilisez le préréglage « Sous 3 jours »)."
                )
            } else if (picked.isAfter(now.clone().add(30, "days"))) {
                errors.push("L'échéance doit être dans les 30 prochains jours.")
            }
        }
    }
    if (!subjectId) errors.push("Choisissez une matière.")

    if (format === "shared") {
        if (draft.slotIds.length === 0) errors.push("Sélectionnez au moins un créneau préféré.")
    } else if (format === "individual" && hours > 0) {
        const dates = draft.requestedDates
        if (!dateCountMatchesHours(draft)) {
            errors.push(
                `Sélectionnez exactement ${hours} date${hours > 1 ? "s" : ""} pour un cours individuel (1 par heure).`
            )
        } else {
            const allFutureAndValid = dates.every((value) => {
                if (typeof value !== "string") return false
                const picked = moment(value, EXTRA_HOURS_DATETIME_FORMAT, true)
                return picked.isValid() && picked.isAfter(now)
            })
            if (!allFutureAndValid) errors.push("Chaque date doit être valide et dans le futur.")
            if (new Set(dates).size !== dates.length) errors.push("Vos dates ne peuvent pas être en double.")
        }
    }

    return errors
}

/** L'élève, tel que les deux étapes le lisent — sa classe et les matières de ses objectifs. */
export type ApiExtraHoursStudent = {
    profile?: { class?: string } | null
    pedagogical_objectives?: Array<{ subject_ids?: Array<string | number> }> | null
    timezone?: string | null
} | null

/**
 * « Quelle matière ? » : les matières des objectifs d'abord, les autres derrière le repli
 * (`ParametersStep.js:79-107`). Les deux listes passent par le même filtre de classe
 * (`min_student_class`) : le formulaire ne propose jamais une matière trop avancée.
 */
export function toExtraHoursSubjects(
    schoolSubjects: ApiSchoolSubject[] | null | undefined,
    student: ApiExtraHoursStudent
): { subjects: UISubject[]; otherSubjects: UISubject[] } {
    const filtered: ApiSchoolSubject[] = filterSchoolSubjectsByClass(schoolSubjects ?? [], student?.profile?.class)

    const objectiveIds = new Set<number>()
    for (const objective of student?.pedagogical_objectives ?? []) {
        for (const id of objective?.subject_ids ?? []) objectiveIds.add(Number(id))
    }

    return {
        subjects: filtered.filter((s) => objectiveIds.has(Number(s.id))).map(toUISubject),
        otherSubjects: filtered.filter((s) => !objectiveIds.has(Number(s.id))).map(toUISubject),
    }
}

/**
 * « Les heures sont exprimées en heure de Paris. » — seulement quand le fuseau de l'élève diffère
 * de celui du serveur (`IndividualDatesStep.js:35-36`).
 */
export function extraHoursTimezoneNotice(
    studentTimezone: string | null | undefined,
    serverTimezone: string | null | undefined
): string | undefined {
    if (!studentTimezone || !serverTimezone || studentTimezone === serverTimezone) return undefined
    return "Les heures sont exprimées en heure de Paris."
}
